import React from "react";
import { useTheme } from "../../contexts/ThemeContext";

interface CleaningSummaryCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  subtitleColor?: string;
  icon: React.ReactNode;
  iconBgColor?: string;
  iconColor?: string;
}

const CARD_GRADIENTS: Record<string, { dark: string; light: string; accent: string }> = {
  green: {
    dark: "bg-gradient-to-br from-[#1c4a45] via-[#143532]/90 to-[#1c4a45] border-[#2A6B64]/40",
    light: "bg-gradient-to-br from-emerald-50 via-emerald-100/50 to-emerald-50 border-emerald-200",
    accent: "bg-emerald-500",
  },
  blue: {
    dark: "bg-gradient-to-br from-[#1c3857] via-[#142940]/90 to-[#1c3857] border-[#2A5280]/40",
    light: "bg-gradient-to-br from-blue-50 via-blue-100/50 to-blue-50 border-blue-200",
    accent: "bg-blue-500",
  },
  orange: {
    dark: "bg-gradient-to-br from-[#423215] via-[#2D2412]/90 to-[#423215] border-[#5E4A1D]/40",
    light: "bg-gradient-to-br from-amber-50 via-amber-100/50 to-amber-50 border-amber-200",
    accent: "bg-amber-500",
  },
  purple: {
    dark: "bg-gradient-to-br from-[#3d2e5a] via-[#2C2142]/90 to-[#3d2e5a] border-[#594485]/40",
    light: "bg-gradient-to-br from-violet-50 via-violet-100/50 to-violet-50 border-violet-200",
    accent: "bg-violet-500",
  },
};

const getCardColorKey = (title: string) => {
  const t = title.toLowerCase();
  if (t.includes("coverage")) return "orange";
  if (t.includes("missed")) return "purple";
  if (t.includes("last cleaned") || t.includes("cleaning sessions")) return "blue";
  return "green";
};

const getPillLabel = (title: string): string | null => {
  const t = title.toLowerCase();
  if (t.includes("coverage")) return "Live";
  if (t.includes("cleaning sessions")) return "Today";
  if (t.includes("trend")) return "Week";
  return null;
};

const CleaningSummaryCard: React.FC<CleaningSummaryCardProps> = ({
  title,
  value,
  subtitle,
  subtitleColor = "text-gray-400",
  icon,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const colorKey = getCardColorKey(title);
  const gradientObj = CARD_GRADIENTS[colorKey] ?? CARD_GRADIENTS.blue;
  const gradient = isDarkMode ? gradientObj.dark : gradientObj.light;
  const accent = gradientObj.accent;
  const pill = getPillLabel(title);

  return (
    <div className={`group relative overflow-hidden rounded-2xl p-4 border shadow-sm transition-all flex-1 min-w-[240px] h-[160px] duration-300 hover:scale-[1.02] ${gradient} ${isDarkMode ? "text-white" : "text-gray-900"}`}>
      {/* Subtle glow effect */}
      {isDarkMode && (
        <div className={`absolute -top-10 -right-10 w-24 h-24 rounded-full blur-3xl opacity-20 ${accent}`} />
      )}

      <div className="relative h-full flex flex-col justify-between">
        <div className="flex items-center justify-between">
          <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${isDarkMode ? "bg-white/10 text-white/90 border border-white/5" : "bg-black/5 text-gray-700 border border-black/5"}`}>
            {icon && React.cloneElement(icon as React.ReactElement, { size: 20 })}
          </div>
          {pill && (
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${isDarkMode ? "bg-white/10 text-white/70 border border-white/5" : "bg-black/10 text-gray-700"}`}>
              {pill}
            </span>
          )}
        </div>

        <div className="mt-2">
          <h3 className={`text-xs font-bold uppercase tracking-widest mb-1 ${isDarkMode ? "text-white/60" : "text-gray-500"}`}>
            {title}
          </h3>
          <div className="flex items-baseline gap-1">
            <p className={`text-3xl font-black tracking-tight ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              {value}
            </p>
          </div>
          <p className={`text-[11px] mt-1 font-medium ${isDarkMode ? "text-white/40" : "text-gray-500"} truncate`}>{subtitle}</p>
        </div>

        <div className={`w-10 h-[3px] rounded-full ${accent} opacity-60`} />
      </div>
    </div>
  );
};

export default CleaningSummaryCard;
